'use client';

import type { CustomerFunnelStage, MarketingActivationFormat, MarketingChannel, MarketingCollaborationModel, MarketingDeliveryActivity, MarketingRole, MarketingTactic, ProductStage } from '@sizzlenz/shared';
import { useRouter } from 'next/router';
import { Columns2, ListFilter, Rows3 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createEmptyInspirationFilters, InspirationFilters, type InspirationFilterKey, type InspirationFilterValue } from '@/components/demand/InspirationFilters';
import { InspirationFeedQuickFilters } from '@/components/demand/InspirationFeedQuickFilters';
import type { QuickFilterPreset } from '@/components/filters/QuickFilters';
import { ExampleCard, contentToDemandExample } from '@/components/demand/ExampleCard';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import type { ApiContent } from '@/lib/api/types';
import { buildInspirationFilterSystemTags, buildInspirationFilterSystemTagGroups } from '@/lib/inspiration-filters';
import { useContentDetailSheet } from '@/lib/hooks/useContentDetailSheet';
import { useSheetManager } from '@/lib/ui';
import { useDemandFeedLayout } from '@/lib/hooks/useDemandFeedLayout';
import { useDemandFeedShuffle } from '@/lib/hooks/useDemandFeedShuffle';
import { useContentList } from '@/lib/hooks/useContent';
import { useEntity } from '@/lib/hooks/useEntities';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import { useSessionState } from '@/components/SessionState';
import { useSession } from '@/lib/auth-client';
import { Badge } from '@/components/ui/badge';
import { Building2, X } from 'lucide-react';
const INSPIRATION_FEED_FILTERS = ['productStages', 'tactics', 'channels', 'deliveryActivities', 'collaborationModels', 'activationFormats'] as const satisfies readonly InspirationFilterKey[];
const DEFAULT_PAGE_SIZE = 10;

/**
 * A subset of {@link InspirationFilterValue} whose values are fixed by the
 * host (e.g. a hub tab configured via JSON). Locked values are always applied to
 * the query and cannot be toggled off or cleared by the user, but the facet's
 * control stays visible so the user can still add other values on top (OR within
 * the facet). To hide a facet entirely, omit it from `visibleFilters`.
 */
export type LockedInspirationFilters = Partial<InspirationFilterValue>;
interface InspirationFeedProps {
  pageSize?: number;
  /**
   * Facet values that are hardcoded and cannot be toggled off or cleared by the
   * user. They always narrow the feed (OR within a facet, AND across facets) and
   * render as checked, non-removable options/chips. The facet control remains
   * visible so the user can add other values on top.
   *
   * Example: `{ tactics: ['collaboration'] }` always applies the collaboration
   * tactic while still letting the user select additional tactics.
   */
  lockedFilters?: LockedInspirationFilters;
  /**
   * Which facet controls to show the user. Defaults to the standard feed set.
   * Use this to hide a facet entirely (locked values still apply even if their
   * facet is not listed here).
   */
  visibleFilters?: readonly InspirationFilterKey[];
  /**
   * Whether locked filters are shown to the user. Defaults to true, matching the
   * previous behavior (locked values render as checked/disabled options and as
   * `Lock` chips). Set false to keep applying locked values to the query while
   * hiding them from the filter UI entirely.
   */
  showLockedFilters?: boolean;
  /**
   * When true, hides the page-level sticky header + full-viewport chrome so the
   * feed can be embedded inside another surface (e.g. a hub tab).
   */
  hideHeader?: boolean;
  /**
   * Show the quick-filter pills row. Defaults to true. Set false to hide it
   * entirely (e.g. from a hub tab that doesn't want shortcuts).
   */
  showQuickFilters?: boolean;
  /**
   * Where the quick-filter pills render relative to the facet filter controls.
   * - `'below'` (default): separate row beneath the filters card (long-standing
   *   layout).
   * - `'above'`: separate row above the filters card, reading as the primary
   *   entry point.
   * - `'withFilters'`: rendered inside the filters card, above the facet
   *   controls, so pills and facets share one card.
   */
  quickFiltersPosition?: 'above' | 'below' | 'withFilters';
  /**
   * Explicit quick-filter presets. When provided, these replace the
   * metadata-driven presets and built-in defaults. Ignored when
   * `showQuickFilters` is false.
   */
  quickFilters?: readonly QuickFilterPreset[];
}

/**
 * Merge locked facet values into a user filter selection. Locked values always
 * win and are appended (deduped) so the query always narrows by them.
 */
function mergeLockedFilters(filters: InspirationFilterValue, locked: LockedInspirationFilters | undefined): InspirationFilterValue {
  if (!locked) return filters;
  const merged = {
    ...filters
  };
  // Indexing by a union key widens the write target to an intersection of all
  // value types, so write through a string-keyed view. Every facet value is a
  // string[], so this is safe.
  const writable = merged as Record<string, string[]>;
  for (const key of Object.keys(locked) as (keyof InspirationFilterValue)[]) {
    const lockedValues = locked[key];
    if (!lockedValues || lockedValues.length === 0) continue;
    const existing = merged[key] as string[];
    writable[key] = Array.from(new Set([...existing, ...lockedValues]));
  }
  return merged;
}
export function InspirationFeed({
  pageSize = DEFAULT_PAGE_SIZE,
  lockedFilters,
  visibleFilters,
  showLockedFilters = true,
  hideHeader = false,
  showQuickFilters = true,
  quickFiltersPosition = 'below',
  quickFilters
}: InspirationFeedProps = {}) {
  const {
    showContentDetail
  } = useContentDetailSheet();
  const {
    closeSheet
  } = useSheetManager();
  const router = useRouter();
  // The resolved path (without query) the feed lives on, captured at mount. Used
  // by the detail-sheet URL sync so the feed works both as the standalone
  // `/inspiration-feed` page and when embedded in another route (e.g. a content
  // hub tab at `/content/collaborations`). We use `asPath` (not `pathname`) so it
  // matches the resolved URLs reported by `router.events`.
  const feedPathRef = useRef(router.asPath.split('?')[0]);
  const {
    layout,
    setLayout
  } = useDemandFeedLayout();
  const {
    sessionHash
  } = useSessionState();
  const {
    data: authSession
  } = useSession();
  // The "Hide saved" toggle only applies to signed-in users (saved/followed
  // state requires a session). Hide it entirely for logged-out visitors.
  const isSignedIn = !!authSession?.user;

  // When on, the feed is ordered by a per-session shuffle (server-side, via
  // sessionHash) instead of newest-first. Keeps the order stable across pages
  // within a session while giving each visit a fresh mix. Defaults to shuffled
  // and persists the choice in local settings.
  const {
    shuffle,
    setShuffle
  } = useDemandFeedShuffle();
  const {
    values,
    handlers,
    isReady
  } = useUrlSync({
    filters: {
      demandRoles: {
        paramName: 'roles',
        defaultValue: [],
        isArray: true
      },
      journeyStages: {
        paramName: 'stages',
        defaultValue: [],
        isArray: true
      },
      productStages: {
        paramName: 'product',
        defaultValue: [],
        isArray: true
      },
      tactics: {
        paramName: 'tactics',
        defaultValue: [],
        isArray: true
      },
      channels: {
        paramName: 'channels',
        defaultValue: [],
        isArray: true
      },
      deliveryActivities: {
        paramName: 'execution',
        defaultValue: [],
        isArray: true
      },
      collaborationModels: {
        paramName: 'collab',
        defaultValue: [],
        isArray: true
      },
      activationFormats: {
        paramName: 'format',
        defaultValue: [],
        isArray: true
      },
      hideFollowing: {
        paramName: 'hideSaved',
        defaultValue: ''
      },
      // Scope the feed to a linked record, encoded as `<objectType>:<idOrSlug>`
      // (e.g. `entity:allpress` or `entity:504`). Content is matched via the
      // `@linked=<objectType>:<id>` systemTag (AND-narrowing). Shown as a
      // removable chip with the record's name; it has no facet field of its own.
      linked: {
        paramName: 'linked',
        defaultValue: ''
      },
      // Route id (slug or numeric id) of the inspiration item shown in the
      // detail sheet. Kept in the URL so the sheet is deep-linkable and browser
      // back/forward opens and closes it.
      item: {
        paramName: 'item',
        defaultValue: ''
      }
    }
  });

  // Toggle: when on, hide inspiration the user has already saved/followed. The
  // exclusion runs server-side (see excludeFollowing → NOT EXISTS subquery) so
  // pagination stays correct.
  const hideFollowing = values.hideFollowing === '1';
  const setHideFollowing = useCallback((next: boolean) => handlers.hideFollowing(next ? '1' : ''), [handlers]);

  // Bridge the flat URL-synced string arrays to the typed filter object the
  // InspirationFilters component expects.
  const filters = useMemo<InspirationFilterValue>(() => ({
    demandRoles: values.demandRoles as MarketingRole[],
    journeyStages: values.journeyStages as CustomerFunnelStage[],
    productStages: values.productStages as ProductStage[],
    demandObjectives: [],
    tactics: values.tactics as MarketingTactic[],
    channels: values.channels as MarketingChannel[],
    deliveryActivities: values.deliveryActivities as MarketingDeliveryActivity[],
    collaborationModels: values.collaborationModels as MarketingCollaborationModel[],
    activationFormats: values.activationFormats as MarketingActivationFormat[]
  }), [values]);

  // Locked values, keyed by facet, as plain string arrays. These are always
  // applied and cannot be toggled off, but the facet control stays visible so
  // the user can still add other values on top (OR within the facet).
  const lockedSelections = useMemo<Partial<Record<InspirationFilterKey, readonly string[]>>>(() => {
    const result: Partial<Record<InspirationFilterKey, readonly string[]>> = {};
    for (const key of Object.keys(lockedFilters ?? {}) as InspirationFilterKey[]) {
      const vals = lockedFilters?.[key];
      if (vals && vals.length > 0) result[key] = vals;
    }
    return result;
  }, [lockedFilters]);

  // Only persist the user-added (non-locked) values to the URL. Locked values are
  // re-applied from config on every render (see `displayFilters`), so writing them
  // to the URL would be redundant and would linger if the config later changes.
  const handleFiltersChange = useCallback((next_0: InspirationFilterValue) => {
    const stripLocked = (key_0: InspirationFilterKey) => {
      const locked = new Set(lockedSelections[key_0] ?? []);
      return (next_0[key_0] as string[]).filter(id => !locked.has(id));
    };
    handlers.demandRoles(stripLocked('demandRoles'));
    handlers.journeyStages(stripLocked('journeyStages'));
    handlers.productStages(stripLocked('productStages'));
    handlers.tactics(stripLocked('tactics'));
    handlers.channels(stripLocked('channels'));
    handlers.deliveryActivities(stripLocked('deliveryActivities'));
    handlers.collaborationModels(stripLocked('collaborationModels'));
    handlers.activationFormats(stripLocked('activationFormats'));
  }, [handlers, lockedSelections]);

  // Which facet controls the user sees. Locked facets stay visible (the user can
  // add more values) — only the explicit `visibleFilters` prop narrows the set.
  const effectiveVisibleFilters = useMemo(() => visibleFilters ?? INSPIRATION_FEED_FILTERS, [visibleFilters]);

  // The filter value shown in the controls and used to build the query: the
  // user's URL selection merged with the locked values (locked values always
  // apply and render as checked/non-removable).
  const displayFilters = useMemo(() => mergeLockedFilters(filters, lockedFilters), [filters, lockedFilters]);

  // True when the host has locked at least one facet value.
  const hasLockedFilters = useMemo(() => Object.keys(lockedSelections).length > 0, [lockedSelections]);

  // Server-side filtering. Two shapes, chosen by whether any facet is locked:
  //
  // - No lock (standard feed): a single flat `systemTags` OR (arrayOverlaps).
  //   Selecting values across facets broadens results — the long-standing feed
  //   behavior.
  // - Locked: `systemTagGroups` — OR within a group, AND across groups. Locked
  //   values and the user's own selection are emitted as SEPARATE groups (not
  //   merged into one OR), so a locked value is always mandatory AND the user's
  //   narrowing still applies. e.g. locked tactic `collaboration` + user tactic
  //   `retail` → [[collaboration], [retail]] = has(collaboration) AND has(retail),
  //   not [[collaboration, retail]] which a collaboration-only record satisfies.
  //   The raw user `filters` (not the locked-merged `displayFilters`) is passed
  //   so the two sources stay in distinct groups.
  const systemTags = useMemo(() => hasLockedFilters ? [] : buildInspirationFilterSystemTags(displayFilters), [hasLockedFilters, displayFilters]);
  const systemTagGroups = useMemo(() => hasLockedFilters ? buildInspirationFilterSystemTagGroups(filters, lockedFilters) : [], [hasLockedFilters, filters, lockedFilters]);

  // Optional "linked" scope. The URL carries `<objectType>:<idOrSlug>`; we split
  // it, resolve the record so we can (a) filter on its numeric id via the
  // `@linked=<objectType>:<id>` systemTag and (b) show its name in the chip.
  const linkedParam = values.linked;
  const linkedRef = useMemo(() => {
    if (!linkedParam) return null;
    const separatorIndex = linkedParam.indexOf(':');
    // Default to `entity` when no type prefix is supplied (back-compat with a
    // bare id/slug), otherwise take the part before the first colon as the type.
    if (separatorIndex === -1) {
      return {
        objectType: 'entity',
        idOrSlug: linkedParam
      };
    }
    return {
      objectType: linkedParam.slice(0, separatorIndex),
      idOrSlug: linkedParam.slice(separatorIndex + 1)
    };
  }, [linkedParam]);
  const clearLinked = useCallback(() => handlers.linked(''), [handlers]);

  // Resolve the linked record's display name + numeric id. Only entity is wired
  // up today; the hook is gated on id so it's a no-op for other types. When a
  // new linkable type is added, resolve it here and fold it into `linkedRecord`.
  const {
    data: linkedEntity
  } = useEntity(linkedRef?.objectType === 'entity' ? linkedRef.idOrSlug : '');
  const linkedRecord = linkedRef?.objectType === 'entity' && linkedEntity ? {
    objectType: 'entity',
    objectId: linkedEntity.id,
    name: linkedEntity.name_1 || `Entity ${linkedEntity.id}`
  } : null;
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useContentList({
    type: ['inspiration', 'case-study'],
    status: 'published',
    limit: pageSize,
    systemTags: systemTags.length > 0 ? systemTags : undefined,
    systemTagGroups: systemTagGroups.length > 0 ? systemTagGroups : undefined,
    // Narrow to content linked to the resolved record. Gated on the numeric
    // id so a slug in the URL still maps to the id used in the systemTag.
    // An array so callers can OR across multiple linked records.
    linked: linkedRecord ? [{
      objectType: linkedRecord.objectType,
      objectId: linkedRecord.objectId
    }] : undefined,
    sessionHash: shuffle ? sessionHash : undefined,
    excludeFollowing: isSignedIn && hideFollowing ? true : undefined
  },
  // Wait until the router is ready so URL-synced filters (roles, tactics,
  // hideSaved, etc.) are resolved before the first fetch. Firing earlier
  // would run once with defaults and again once the query settles, causing
  // a duplicate content list request.
  //
  // When a `linked` param is present, also wait until the record resolves so
  // the first fetch already carries the linked filter — otherwise we'd flash
  // the unfiltered feed, then refetch once the id lands.
  {
    enabled: isReady && (!linkedParam || !!linkedRecord)
  });
  const feedContent = useMemo<ApiContent[]>(() => (data?.pages ?? []).flatMap(page => page.content), [data]);

  // ---------------------------------------------------------------------------
  // URL <-> detail sheet sync
  //
  // The open item is stored in the `item` query param (the same slug/id used to
  // route to `/content/[id]`). The URL is the single source of truth:
  // - Clicking a card sets `item` (pushes history) → effect opens the sheet.
  // - Closing the sheet clears `item` → effect closes the sheet.
  // - Browser back/forward changes `item` → effect opens/closes to match.
  // - A direct link with `?item=...` opens the feed and then the sheet.
  // ---------------------------------------------------------------------------
  const openItemId = values.item;

  // Sheet ids are derived from the numeric content id (see useContentDetailSheet),
  // so map the route id (which may be a slug) to its content object when known.
  const contentByRouteId = useMemo(() => {
    const map = new Map<string, ApiContent>();
    for (const content of feedContent) {
      map.set(String(content.id), content);
      if (content.slug) map.set(content.slug, content);
    }
    return map;
  }, [feedContent]);

  // Tracks the route id the sheet is currently showing, so the effect only
  // reacts to genuine changes (not to filter/list re-renders).
  const openedRouteIdRef = useRef<string>('');
  // When true, the sheet's onClose was triggered by a URL change (back button
  // or programmatic clear) and must not push another history entry.
  const suppressCloseSyncRef = useRef(false);
  // When true, a navigation to a different page is in flight (e.g. the Edit
  // button inside the sheet routes to /admin/content/[id]/edit). The sheet
  // closes as a side effect of that navigation, and we must NOT clear the
  // `item` param — doing so would push the feed URL and cancel the navigation.
  const navigatingAwayRef = useRef(false);
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      // Compare pathnames only (ignore query). Clearing the sheet param is a
      // shallow, same-page change; anything landing on a different pathname is
      // a real navigation we must not interfere with.
      const nextPath = url.split('?')[0];
      navigatingAwayRef.current = nextPath !== feedPathRef.current;
    };
    const handleRouteChangeDone = () => {
      navigatingAwayRef.current = false;
    };
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeDone);
    router.events.on('routeChangeError', handleRouteChangeDone);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeDone);
      router.events.off('routeChangeError', handleRouteChangeDone);
    };
  }, [router.events]);
  const closeItem = useCallback(() => {
    // Skip when the close was triggered by our own URL sync (back button or
    // programmatic close), or when the sheet is closing because the user is
    // navigating to another page (e.g. the Edit link inside the sheet).
    if (suppressCloseSyncRef.current) return;
    if (navigatingAwayRef.current) return;
    if (router.asPath.split('?')[0] !== feedPathRef.current) return;
    handlers.item('');
  }, [handlers, router.asPath]);
  const openItemInSheet = useCallback((routeId: string) => {
    const known = contentByRouteId.get(routeId);
    // keepOpenOnRouteChange stays false: opening only changes the query
    // (shallow), which does not change the pathname the SheetManager watches,
    // so the sheet is not auto-closed. A real navigation to another page
    // (e.g. "Open full page") still changes the pathname and closes it.
    showContentDetail(known ?? routeId, {
      onClose: closeItem
    });
  }, [contentByRouteId, showContentDetail, closeItem]);
  useEffect(() => {
    if (!isReady) return;

    // Nothing changed since the last sync — ignore unrelated re-renders.
    if (openItemId === openedRouteIdRef.current) return;

    // Close the previously-open sheet (if any) without pushing a history entry.
    const previous = openedRouteIdRef.current;
    if (previous) {
      const prevContent = contentByRouteId.get(previous);
      const prevSheetId = `content-detail-${prevContent ? prevContent.id : previous}`;
      suppressCloseSyncRef.current = true;
      closeSheet(prevSheetId);
      suppressCloseSyncRef.current = false;
    }
    openedRouteIdRef.current = openItemId;
    if (openItemId) {
      openItemInSheet(openItemId);
    }
  }, [isReady, openItemId, contentByRouteId, closeSheet, openItemInSheet]);
  const clearFilters = () => handleFiltersChange(createEmptyInspirationFilters());

  // While a `linked` param is set but the record hasn't resolved, the list query
  // is disabled (see `enabled` above) — treat that as loading so we don't flash
  // an empty state before the filtered fetch runs.
  const linkedResolving = !!linkedParam && !linkedRecord;
  const showInitialLoading = !isReady || isLoading || linkedResolving;

  // Display name for the linked chip. Falls back to the raw id/slug while the
  // record name is unavailable.
  const linkedLabel = linkedRecord ? linkedRecord.name : linkedRef?.idOrSlug || linkedParam;

  // The quick-filter pills element, positioned per `quickFiltersPosition`.
  // 'above'/'below' render it as a standalone row (with margin); 'withFilters'
  // passes it into the filters card via the `quickFilters` slot (no margin —
  // the card provides its own spacing).
  const quickFiltersEl = showQuickFilters ? <InspirationFeedQuickFilters className={quickFiltersPosition === 'above' ? 'mb-3' : quickFiltersPosition === 'below' ? 'mt-3' : undefined} value={displayFilters} presets={quickFilters} onChange={handleFiltersChange} /> : null;
  const body = <>
      {quickFiltersPosition === 'above' && quickFiltersEl}

      <InspirationFilters value={displayFilters} onChange={handleFiltersChange} resultCount={feedContent.length} totalCount={feedContent.length} visibleFilters={effectiveVisibleFilters} lockedSelections={lockedSelections} showLockedFilters={showLockedFilters} quickFilters={quickFiltersPosition === 'withFilters' ? quickFiltersEl : undefined} trailing={<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {isSignedIn && <div className="flex items-center gap-2">
                <Switch id="hide-following" checked={hideFollowing} onCheckedChange={setHideFollowing} className="cursor-pointer" />
                <Label htmlFor="hide-following" className="cursor-pointer whitespace-nowrap text-sm font-medium text-gray-500">
                  Hide saved
                </Label>
              </div>}

            <Select value={shuffle ? 'shuffled' : 'latest'} onValueChange={next_1 => setShuffle(next_1 === 'shuffled')}>
              <SelectTrigger size="sm" aria-label="Sort feed" className="cursor-pointer gap-1 border-0 bg-transparent px-1 text-sm text-gray-500 shadow-none hover:text-gray-900 focus-visible:ring-0 dark:bg-transparent dark:hover:bg-transparent">
                <span className="text-gray-500">Sort by:</span>
                <SelectValue className="font-semibold text-gray-900" />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="shuffled" className="cursor-pointer">
                  Shuffled
                </SelectItem>
                <SelectItem value="latest" className="cursor-pointer">
                  Latest
                </SelectItem>
              </SelectContent>
            </Select>

            <ToggleGroup type="single" value={layout} onValueChange={next_2 => {
        if (next_2 === 'side-by-side' || next_2 === 'stacked') setLayout(next_2);
      }} variant="outline" aria-label="Card layout">
              <ToggleGroupItem value="side-by-side" aria-label="Side by side layout" className="cursor-pointer">
                <Columns2 className="size-4" aria-hidden="true" />
              </ToggleGroupItem>
              <ToggleGroupItem value="stacked" aria-label="Stacked layout" className="cursor-pointer">
                <Rows3 className="size-4" aria-hidden="true" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>} />

      {quickFiltersPosition === 'below' && quickFiltersEl}

      {/* Linked-entity scope chip. Has no facet field of its own — it's driven
            by the `linked` URL param and is removable to clear the scope. */}
      {linkedParam && <div className="mt-3 flex flex-wrap items-center gap-2" aria-live="polite">
          <span className="text-sm text-gray-500">Showing inspiration linked to</span>
          <Badge variant="secondary" className="gap-1 pl-2">
            <Building2 className="size-3 shrink-0 text-gray-500" aria-hidden="true" />
            {linkedLabel}
            <button type="button" className="cursor-pointer rounded-full p-0.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400" aria-label={`Remove ${linkedLabel} filter`} onClick={clearLinked}>
              <X className="size-3" aria-hidden="true" />
            </button>
          </Badge>
        </div>}

      {showInitialLoading ? <div className="mt-6 flex flex-col items-center gap-5" aria-busy="true" aria-live="polite">
          {Array.from({
        length: 3
      }).map((_, index) => <div key={index} className="h-72 w-full max-w-md animate-pulse rounded-2xl border border-gray-200 bg-white" />)}
        </div> : isError ? <Empty className="mt-6 border border-gray-200 bg-white">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ListFilter aria-hidden="true" />
            </EmptyMedia>
            <EmptyTitle>Couldn't load inspiration</EmptyTitle>
            <EmptyDescription>Something went wrong. Try again in a moment.</EmptyDescription>
          </EmptyHeader>
        </Empty> : feedContent.length > 0 ? <>
          <div className="mt-6 flex flex-col items-center gap-5">
            {feedContent.map(content_0 => <ExampleCard key={content_0.id} showHighlights variant="intelligence" example={contentToDemandExample(content_0)} detailMode="sheet" onOpen={() => handlers.item(content_0.slug || String(content_0.id))} moreDetailsLabel="Open" widthClass="w-full" className="w-full" layout={layout} showFollow demandFacets={['objective', 'tactic', 'channel']} />)}
          </div>

          {hasNextPage && <div className="mt-8 flex justify-center">
              <Button type="button" variant="outline" className="cursor-pointer" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                {isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            </div>}
        </> : <Empty className="mt-6 border border-gray-200 bg-white">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ListFilter aria-hidden="true" />
            </EmptyMedia>
            <EmptyTitle>No matching inspiration yet! Check back regularly</EmptyTitle>
            <EmptyDescription>
              Try removing one or two filters to broaden the inspiration shown.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button type="button" className="cursor-pointer" onClick={clearFilters}>
              Clear all filters
            </Button>
          </EmptyContent>
        </Empty>}
    </>;

  // Embedded mode (e.g. inside a hub tab): render just the feed body without the
  // full-viewport background or sticky page header.
  if (hideHeader) {
    return <div className="mx-auto max-w-4xl">{body}</div>;
  }
  return <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-4 py-4 text-center sm:px-6">
          <h1 className="text-2xl font-black tracking-tight text-gray-950 sm:text-3xl">
            Get Inspired. Make your next move.
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">{body}</main>
    </div>;
}